import React, { useRef, useState, useCallback } from 'react';
import { ArrowUp, CaretRight, Plus, Stop } from '@phosphor-icons/react';

import { RichTextInput, type RichTextInputHandle } from '../rich-text-input';
import { cn } from '../../lib/utils';
import { useConversation } from './ConversationContext';
import type { Attachment, AttachmentAction, Collection, ContextPill } from './types';
import { ContextPillBadge } from './ContextPillBadge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';

export interface ConversationInputProps {
  placeholder?: string;
  attachments?: Attachment[];
  collections?: Collection[];
  contextPills?: ContextPill[];
  disabled?: boolean;
  isElevated?: boolean;
  className?: string;
}

const COLLECTION_SEARCH_THRESHOLD = 5;

export const ConversationInput = ({
  placeholder = 'What can we help with today?',
  attachments = [],
  collections = [],
  contextPills = [],
  disabled = false,
  isElevated = false,
  className,
}: ConversationInputProps) => {
  const { callbacks, isStreaming } = useConversation();
  const [hasText, setHasText] = useState(false);
  const latestMarkdown = useRef('');
  const editorRef = useRef<RichTextInputHandle>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const collectionCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collectionHasFocus = useRef(false);

  const openCollectionPicker = useCallback(() => {
    if (collectionCloseTimer.current) {
      clearTimeout(collectionCloseTimer.current);
      collectionCloseTimer.current = null;
    }
    setCollectionPickerOpen(true);
  }, []);

  const scheduleCollectionPickerClose = useCallback(() => {
    collectionCloseTimer.current = setTimeout(() => {
      if (!collectionHasFocus.current) {
        setCollectionPickerOpen(false);
      }
    }, 150);
  }, []);
  const isDisabled = disabled;

  const handleSend = useCallback(
    (markdown?: string) => {
      const trimmed = (markdown ?? latestMarkdown.current).trim();
      if (!trimmed || isDisabled || isStreaming) return;
      callbacks.onSend?.(trimmed, attachments.length > 0 ? attachments : undefined);
      editorRef.current?.clear();
      editorRef.current?.focus();
    },
    [isDisabled, isStreaming, callbacks, attachments],
  );

  const handleStop = useCallback(() => {
    callbacks.onStop?.();
  }, [callbacks]);

  const handleAttachmentAction = useCallback(
    (action: AttachmentAction) => {
      callbacks.onAttachmentAction?.(action);
    },
    [callbacks],
  );

  const handleCollectionSelect = useCallback(
    (collection: Collection) => {
      setCollectionPickerOpen(false);
      setMenuOpen(false);
      handleAttachmentAction({ type: 'select-collection', collection });
    },
    [handleAttachmentAction],
  );

  return (
    <div className={cn('', className)}>
      <div
        className={cn(
          'relative rounded-t-2xl rounded-b-lg bg-gradient-yellowfin p-2 transition-shadow duration-300',
          isElevated && 'shadow-[0_-20px_25px_-5px_rgba(0,0,0,0.1),0_-8px_10px_-6px_rgba(0,0,0,0.1)]',
        )}
      >
        <div className="rounded-t-lg rounded-b bg-surface shadow-md">
          {contextPills.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 pt-3">
              {contextPills.map((pill) => (
                <ContextPillBadge key={pill.id} pill={pill} onRemove={callbacks.onContextPillRemove} />
              ))}
            </div>
          )}

          <RichTextInput
            ref={editorRef}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            className="w-full bg-transparent p-4 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            onChange={(markdown) => {
              latestMarkdown.current = markdown;
              setHasText(markdown.trim().length > 0);
            }}
            onSubmit={handleSend}
          />

          <div className="flex items-center justify-between px-2 pb-2">
            <div className="flex items-center gap-1">
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild disabled={disabled || isStreaming}>
                  <button
                    type="button"
                    className={cn(
                      'inline-flex h-6 w-6 items-center justify-center rounded text-default hover:bg-muted transition-all',
                      menuOpen && 'shadow-md dark:shadow-md',
                    )}
                    disabled={disabled || isStreaming}
                  >
                    <Plus className={cn('h-4 w-4 transition-transform duration-200', menuOpen && '-rotate-45')} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="right"
                  className="rounded border-none shadow-md bg-surface p-2 min-w-[200px] dark:border-none dark:shadow-md"
                >
                  {collections.length > 0 && (
                    <>
                      <Popover open={collectionPickerOpen} onOpenChange={setCollectionPickerOpen}>
                        <PopoverTrigger asChild>
                          <DropdownMenuItem
                            className="p-2"
                            onSelect={(e) => e.preventDefault()}
                            onMouseEnter={openCollectionPicker}
                            onMouseLeave={scheduleCollectionPickerClose}
                            onFocus={openCollectionPicker}
                          >
                            Collection
                            <CaretRight className="ml-auto h-4 w-4" />
                          </DropdownMenuItem>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[200px] rounded border-none p-0 shadow-md dark:border-none dark:shadow-md"
                          side="right"
                          align="start"
                          onMouseEnter={openCollectionPicker}
                          onMouseLeave={scheduleCollectionPickerClose}
                          onFocusCapture={() => {
                            collectionHasFocus.current = true;
                          }}
                          onBlurCapture={() => {
                            collectionHasFocus.current = false;
                            scheduleCollectionPickerClose();
                          }}
                        >
                          <Command>
                            {collections.length > COLLECTION_SEARCH_THRESHOLD && (
                              <CommandInput placeholder="Search collections..." />
                            )}
                            <CommandList>
                              <CommandEmpty>No collections found.</CommandEmpty>
                              <CommandGroup>
                                {collections.map((collection) => (
                                  <CommandItem
                                    key={collection.id}
                                    value={collection.label}
                                    onSelect={() => handleCollectionSelect(collection)}
                                  >
                                    {collection.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem className="p-2" onSelect={() => handleAttachmentAction({ type: 'attach-tab' })}>
                    Attach tab
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="p-2" onSelect={() => handleAttachmentAction({ type: 'screenshot' })}>
                    Take a screenshot
                  </DropdownMenuItem>
                  <DropdownMenuItem className="p-2" onSelect={() => handleAttachmentAction({ type: 'record-video' })}>
                    Record a video
                  </DropdownMenuItem>
                  <DropdownMenuItem className="p-2" onSelect={() => handleAttachmentAction({ type: 'add-file' })}>
                    Add image or file
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {isStreaming ? (
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded transition-colors bg-input text-primary-foreground hover:bg-input/90"
                onClick={handleStop}
                disabled={disabled}
              >
                <Stop className="h-4 w-4" weight="fill" />
              </button>
            ) : (
              <button
                type="button"
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded transition-colors',
                  hasText ? 'bg-input text-primary-foreground hover:bg-input/90' : 'bg-muted text-muted-foreground',
                )}
                onClick={() => handleSend()}
                disabled={isDisabled || !hasText}
              >
                <ArrowUp className="h-4 w-4" weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
